/**
 * Amenity validation policy — Zod-free primitive constants (SCRUM-137 / DS-010-1).
 *
 * Single source of truth for the amenity-catalog category enum and label
 * length bounds, shared between:
 *   - `@artemis/validators`' `amenityCategorySchema` / `amenityLabelSchema`
 *     (Zod, packages/validators/src/property/amenity.ts), and
 *   - the property domain `AmenityCategory` / `AmenityLabel` value objects
 *     (apps/api/src/modules/property/domain/amenity/value-objects/), and
 *   - the DB CHECK constraints on `property.amenity_catalog`
 *     (category IN (...) and char_length(label) BETWEEN MIN AND MAX).
 *
 * The domain layer imports these constants through the
 * `@artemis/validators/policies` subpath only; it does NOT pull in Zod at
 * runtime (AUDIT-107 boundary discipline). The policies module is pure-TS
 * primitives.
 *
 * `AMENITY_CATEGORY_VALUES` is byte-identical to the category CHECK list in
 * the `amenity_catalog` migration; keep all three in lockstep when extending.
 */
export const AMENITY_CATEGORY_VALUES = [
    'basics',
    'bathroom',
    'bedroom-and-laundry',
    'entertainment',
    'family',
    'heating-and-cooling',
    'home-safety',
    'internet-and-office',
    'kitchen-and-dining',
    'location',
    'outdoor',
    'parking-and-facilities',
    'accessibility',
];
/**
 * Provenance of an amenity-catalog row (SCRUM-262 / CQ-262-3).
 *
 * `confirmed-official` — sourced directly from the OTA's published amenity
 * catalog; `secondary-sourced` — reconstructed from secondary evidence
 * (help-center copy, listing samples). Sorted ascending; byte-identical to
 * the `provenance IN (...)` CHECK on `property.amenity_catalog` and the
 * `amenityProvenanceSchema` Zod enum.
 */
export const AMENITY_PROVENANCE_VALUES = [
    'confirmed-official',
    'secondary-sourced',
];
export const AMENITY_LABEL_MIN_LENGTH = 1;
export const AMENITY_LABEL_MAX_LENGTH = 120;
/**
 * Max codepoint length of a per-property amenity note (SCRUM-138 / DS-010-2).
 *
 * Shared source of truth between:
 *   - the property domain `AmenityNote` value object
 *     (apps/api/src/modules/property/domain/amenity/value-objects/amenity-note.vo.ts),
 *   - the `char_length(note) <= 500` DB CHECK on `property.property_amenity`.
 *
 * No Zod payload schema lives here yet — the HTTP request schema for the
 * set-amenities endpoint is deferred to SCRUM-140 (DS-010-4).
 */
export const AMENITY_NOTE_MAX_LENGTH = 500;
//# sourceMappingURL=amenity.js.map